import { Card, Pill, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import CircleIcon from "@mui/icons-material/Circle";
import { Box, CardActionArea, CardContent, Stack } from "@mui/material";
// FIXME: Automatically disabled during ESLint config update
// eslint-disable-next-line import/no-named-as-default
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { CHAT_PAGE_FULL_PATH } from "@src/appV2/Chat/paths";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { FEATURE_FLAG_DEFAULT_VALUES } from "@src/appV2/FeatureFlags/constants";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { getPlacementChatPath } from "@src/appV2/redesign/Placements/paths";
import pluralize from "pluralize";
import { generatePath, useHistory } from "react-router-dom";

import { FRIEND_CHAT_CUSTOM_TYPE, WORKPLACE_GROUP_CHANNEL_SUFFIX } from "../constants";
import { type ChatChannel } from "../types";
import { isWorkplaceGroupChannel } from "../utils";
import { getLastMessageDate } from "../utils/getLastMessageDate";
import { usePendingShiftInviteCountByWorkplace } from "./hooks/usePendingShiftInviteCountByWorkplace";
import { LastMessagePreview } from "./LastMessagePreview";

interface ChatCardProps {
  channel: ChatChannel;
}

export function ChatListCard(props: ChatCardProps) {
  const { channel } = props;
  const history = useHistory();
  const sendBirdState = useSendbirdStateContext();
  const sdk = sendBirdState?.stores?.sdkStore?.sdk;
  const { facilityId, facilityName, placementTitle } = channel.metadata ?? {};
  const pendingShiftInviteCount = usePendingShiftInviteCountByWorkplace(facilityId);

  const clipboardGuidesConfig = useCbhFlag(CbhFeatureFlag.ALCHEMY_CLIPBOARD_GUIDES_EXPERIMENT, {
    defaultValue: FEATURE_FLAG_DEFAULT_VALUES[CbhFeatureFlag.ALCHEMY_CLIPBOARD_GUIDES_EXPERIMENT],
  });

  // Friends chat is deprecated, but these channels may still exist in the backend
  // Prevent these from rendering
  if (channel.customType === FRIEND_CHAT_CUSTOM_TYPE) {
    return null;
  }

  const workplaceName = channel.shift?.facility?.name ?? facilityName ?? channel.name;

  function getWorkplaceIdFromGroupChannel(channelUrl: string): string {
    return channelUrl.slice(0, -WORKPLACE_GROUP_CHANNEL_SUFFIX.length);
  }

  function isClipboardGuidesChannel(channelUrl: string): boolean {
    const suffix = `_${clipboardGuidesConfig.channelUrlSuffix}`;
    return channelUrl.endsWith(suffix) && channelUrl.length > suffix.length;
  }

  function getWorkplaceIdFromClipboardGuidesChannel(channelUrl: string): string {
    return channelUrl.slice(0, -(clipboardGuidesConfig.channelUrlSuffix.length + 1));
  }

  function getChannelDisplayName(): string {
    if (isWorkplaceGroupChannel(channel.url)) {
      return `Group: ${workplaceName}`;
    }

    if (isClipboardGuidesChannel(channel.url)) {
      return `Community at ${workplaceName}`;
    }

    return workplaceName;
  }

  const channelDisplayName = getChannelDisplayName();

  async function onChannelClick(channelUrl: string, channelCustomType: string | null) {
    if (isWorkplaceGroupChannel(channelUrl)) {
      const workplaceId = getWorkplaceIdFromGroupChannel(channelUrl);
      history.push({
        pathname: generatePath(DeprecatedGlobalAppV1Paths.WORKPLACE_GROUP_CHAT, { workplaceId }),
      });
    } else if (isClipboardGuidesChannel(channelUrl)) {
      const workplaceId = getWorkplaceIdFromClipboardGuidesChannel(channelUrl);
      history.push({
        pathname: generatePath(DeprecatedGlobalAppV1Paths.CLIPBOARD_GUIDES_CHAT, { workplaceId }),
        state: {
          channelUrl,
          workplaceName,
        },
      });
    } else if (channelCustomType === "placements") {
      const [facilityId, , placementId] = channelUrl.split("_");
      history.push(
        getPlacementChatPath(
          placementId,
          facilityId,
          "chat-with-placement-candidate-via-chat-channels"
        )
      );
    } else {
      history.push(generatePath(CHAT_PAGE_FULL_PATH, { facilityId: channelUrl.split("_")[0] }));
    }
  }

  return (
    <Card outlined variant="quaternary">
      <CardActionArea
        onClick={async () => {
          await onChannelClick(channel.url, channel.customType);
        }}
      >
        <CardContent>
          <Stack direction="row" spacing={4} alignItems="flex-start" width="100%">
            <Stack spacing={0.5} width="100%">
              <Stack
                direction="row"
                spacing={2}
                alignItems="flex-start"
                justifyContent="space-between"
              >
                <Text bold variant="body1" sx={{ pb: 2 }}>
                  {channelDisplayName}
                </Text>

                <Stack direction="row" alignItems="center" gap={2}>
                  <Text
                    variant="body2"
                    sx={(theme) => ({
                      color: theme.palette.text.secondary,
                      textWrap: "nowrap",
                    })}
                  >
                    {getLastMessageDate(channel)}
                  </Text>
                  {channel.unreadMessageCount > 0 && <CircleIcon sx={{ fontSize: "0.5rem" }} />}
                </Stack>
              </Stack>

              <Text
                variant="body2"
                sx={(theme) => ({
                  color: theme.palette.text.secondary,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  width: "100%",
                })}
              >
                <LastMessagePreview channel={channel} currentUserId={sdk.currentUser?.userId} />
              </Text>

              {isDefined(facilityId) &&
                channel.customType !== "placements" &&
                pendingShiftInviteCount > 0 && (
                  <Box>
                    <Pill
                      variant="filled"
                      size="small"
                      label={`${pendingShiftInviteCount} pending shift ${pluralize("invite", pendingShiftInviteCount)}`}
                      backgroundColor={(theme) => theme.palette.shift?.invite.background}
                      color={(theme) => theme.palette.shift?.invite.text}
                      outlineColor={(theme) => theme.palette.shift?.invite.border}
                      sx={{
                        marginTop: 4,
                        width: "fit-content",
                      }}
                    />
                  </Box>
                )}

              {channel.customType === "placements" && placementTitle && (
                <Text variant="body2">Job Listing: {placementTitle}</Text>
              )}

              {isWorkplaceGroupChannel(channel.url) && (
                <Box>
                  <Pill
                    variant="filled"
                    size="small"
                    label="Professionals Only"
                    sx={{
                      marginTop: 4,
                      width: "fit-content",
                      background: (theme) => theme.palette.workplaceGroupChats?.gradient,
                    }}
                  />
                </Box>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
